import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  output,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ChecklistHttpService } from '../../../../services/checklist-http.service';
import { OfflineStorageService } from '../../../../services/offline/offline-storage.service';
import { BlobPreviewModalComponent } from '../../shared/blob-preview-modal.component';

@Component({
  selector: 'app-image-preview-modal',
  standalone: true,
  imports: [BlobPreviewModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-blob-preview-modal
      [objectUrl]="imageUrl"
      [loading]="loading"
      (close)="close.emit()"
      (keydown.arrowleft)="showPrevious()"
      (keydown.arrowright)="showNext()"
      (touchstart)="onTouchStart($event)"
      (touchend)="onTouchEnd($event)">
      @if (hasPrevious) {
        <button
          type="button"
          class="absolute left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
          (click)="showPrevious()">
          <span class="text-2xl text-white">&#8249;</span>
        </button>
      }

      @if (hasNext) {
        <button
          type="button"
          class="absolute right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
          (click)="showNext()">
          <span class="text-2xl text-white">&#8250;</span>
        </button>
      }
    </app-blob-preview-modal>
  `,
})
export class ImagePreviewModalComponent
  implements OnInit, OnDestroy, OnChanges
{
  readonly fileId = input.required<string>();
  readonly fileIds = input<string[]>([]);
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly close = output<void>();
  readonly navigate = output<string>();

  loading = true;
  imageUrl: string | null = null;
  private touchStartX: number | null = null;

  private readonly http = inject(ChecklistHttpService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private destroyed = false;
  private loadGeneration = 0;

  ngOnInit(): void {
    this.loadImage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileId'] && !changes['fileId'].firstChange) {
      this.loadImage();
    }
  }

  get currentIndex(): number {
    return this.fileIds().indexOf(this.fileId());
  }

  get hasPrevious(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return (
      this.currentIndex >= 0 && this.currentIndex < this.fileIds().length - 1
    );
  }

  showPrevious(): void {
    if (!this.hasPrevious) return;
    this.navigate.emit(this.fileIds()[this.currentIndex - 1]);
  }

  showNext(): void {
    if (!this.hasNext) return;
    this.navigate.emit(this.fileIds()[this.currentIndex + 1]);
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0]?.clientX ?? null;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.touchStartX == null) return;

    const touchEndX = event.changedTouches[0]?.clientX;
    if (touchEndX == null) {
      this.touchStartX = null;
      return;
    }

    const deltaX = touchEndX - this.touchStartX;
    this.touchStartX = null;

    if (Math.abs(deltaX) < 40) return;

    if (deltaX < 0) {
      this.showNext();
    } else {
      this.showPrevious();
    }
  }

  private loadImage(): void {
    void this.loadImageBlob();
  }

  private async loadImageBlob(): Promise<void> {
    const generation = ++this.loadGeneration;
    const fileId = this.fileId();

    this.loading = true;
    this.revokeImageUrl();
    this.cdr.markForCheck();

    const cachedBlob = await this.getCachedBlob(fileId);
    if (!this.isCurrentLoad(fileId, generation)) {
      return;
    }

    if (cachedBlob) {
      this.setImageBlob(cachedBlob);
      return;
    }

    try {
      const blob = await firstValueFrom(this.http.getFileBlob(fileId));
      if (this.isCurrentLoad(fileId, generation)) {
        this.setImageBlob(blob);
      }
    } catch {
      if (this.isCurrentLoad(fileId, generation)) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.revokeImageUrl();
  }

  private revokeImageUrl(): void {
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
    }
  }

  private async getCachedBlob(fileId: string): Promise<Blob | null> {
    try {
      return await this.offlineStorage.getBlob(fileId);
    } catch {
      return null;
    }
  }

  private setImageBlob(blob: Blob): void {
    this.revokeImageUrl();
    this.imageUrl = URL.createObjectURL(blob);
    this.loading = false;
    this.cdr.markForCheck();
  }

  private isCurrentLoad(fileId: string, generation: number): boolean {
    return (
      !this.destroyed &&
      this.fileId() === fileId &&
      this.loadGeneration === generation
    );
  }
}
